/**
 * Kit add-ons: lets the customer pick one add-on kit next to the buy buttons. The selected kit is appended to the
 * main product form request, so both products are added to the cart at once.
 *
 * IMPLEMENTATION NOTE: the add to cart is handled by the HS Slide Cart app, which reads the named fields of the form
 * by itself (it does not use "new FormData(form)", so the "formdata" event never fires). The app natively supports
 * "items[x][id]" fields though, so the selection is mirrored as hidden inputs attached to the main product form. The
 * "formdata" listener is kept for the theme's own product form, used when the app is disabled.
 */
if (!window.customElements.get("kit-addons")) {
  class KitAddons extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const options = { signal: this._abortController.signal };

      this._basePrice = parseInt(this.getAttribute("base-price")) || 0;
      this.addEventListener("change", this._onChange.bind(this), options);

      // The form may be rendered after this element, so we wait for the DOM to be ready
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", this._bindForm.bind(this), options);
      } else {
        this._bindForm();
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get inputs() {
      return Array.from(this.querySelectorAll(".kit-addons__input"));
    }

    get selectedInput() {
      return this.inputs.find((input) => input.checked);
    }

    _bindForm() {
      this._form = document.forms[this.getAttribute("form")];

      if (!this._form) {
        return;
      }

      const options = { signal: this._abortController.signal };

      this._form.addEventListener("formdata", this._onFormData.bind(this), options);
      this._form.addEventListener("change", () => this._syncInputs(), options);
      // Capture phase on the document, so the fields are up to date before any handler bound on the button itself
      document.addEventListener("click", (event) => {
        if (event.target.closest?.('[type="submit"]')?.form === this._form) {
          this._syncInputs();
        }
      }, { ...options, capture: true });
      this._form.addEventListener("variant:change", (event) => {
        this._basePrice = event.detail.variant ? event.detail.variant["price"] : this._basePrice;
        // The theme resets the button label on variant change, so we update it right after
        setTimeout(() => {
          this._form.querySelector("[data-kit-label]")?.removeAttribute("data-kit-label");
          this._syncInputs();
          this._updateButton();
        });
      }, options);

      this._syncInputs();
      this._updateButton();
    }

    _onChange(event) {
      if (!event.target.classList.contains("kit-addons__input")) {
        return;
      }

      // Kits are mutually exclusive, but (unlike radios) the selected one can be unchecked
      this.inputs.forEach((input) => {
        if (input !== event.target) {
          input.checked = false;
        }
      });

      this._syncInputs();
      this._updateButton();
    }

    /**
     * Mirror the selection as hidden "items[x]" fields attached to the main product form
     */
    _syncInputs() {
      this.querySelectorAll(".kit-addons__field").forEach((field) => field.remove());

      const selectedInput = this.selectedInput,
        mainVariantId = this._form?.querySelector('[name="id"]')?.value;

      if (!selectedInput || !mainVariantId) {
        return;
      }

      const fields = {
        "items[0][id]": mainVariantId,
        "items[0][quantity]": this._form.elements.namedItem("quantity")?.value || 1,
        "items[1][id]": selectedInput.value,
        "items[1][quantity]": 1
      };

      Object.entries(fields).forEach(([name, value]) => {
        const field = document.createElement("input");
        field.type = "hidden";
        field.className = "kit-addons__field";
        field.name = name;
        field.value = value;
        field.setAttribute("form", this._form.getAttribute("id"));
        this.append(field);
      });
    }

    /**
     * Turn the single product request into a multiple items one ("items[0]" is the main product, "items[1]" the kit)
     */
    _onFormData(event) {
      const selectedInput = this.selectedInput,
        formData = event.formData,
        mainVariantId = formData.get("id");

      if (!selectedInput || !mainVariantId) {
        return;
      }

      const entries = Array.from(formData.entries());

      formData.set("items[0][id]", mainVariantId);
      formData.set("items[0][quantity]", formData.get("quantity") || 1);

      entries.forEach(([key, value]) => {
        if (key === "id" || key === "quantity") {
          formData.delete(key);
        } else if (key === "selling_plan") {
          formData.delete(key);
          formData.append("items[0][selling_plan]", value);
        } else if (key.startsWith("properties[")) {
          formData.delete(key);
          formData.append(`items[0]${key.replace("properties[", "[properties][")}`, value);
        }
      });

      formData.set("items[1][id]", selectedInput.value);
      formData.set("items[1][quantity]", 1);
    }

    _updateButton() {
      const button = this._form?.querySelector('buy-buttons button[type="submit"]')
        || document.querySelector(`buy-buttons[form="${this.getAttribute("form")}"] button[type="submit"]`);

      if (!button || button.disabled) {
        return;
      }

      const buttonText = button.getAttribute("is") === "custom-button" && button.firstElementChild ? button.firstElementChild : button,
        selectedInput = this.selectedInput;

      if (!this._defaultButtonLabel || !buttonText.hasAttribute("data-kit-label")) {
        this._defaultButtonLabel = buttonText.innerHTML;
      }

      if (selectedInput) {
        const total = this._basePrice + (parseInt(selectedInput.getAttribute("data-price")) || 0),
          label = this.getAttribute("button-label") || this._defaultButtonLabel;

        buttonText.innerHTML = `${label} &ndash; ${this._formatMoney(total)}`;
        buttonText.setAttribute("data-kit-label", "");
      } else if (buttonText.hasAttribute("data-kit-label")) {
        buttonText.innerHTML = this._defaultButtonLabel;
        buttonText.removeAttribute("data-kit-label");
      }
    }

    _formatMoney(cents) {
      const format = this.getAttribute("money-format") || "${{amount}}";

      const withDelimiters = (precision, thousands, decimal) => {
        const parts = (cents / 100).toFixed(precision).split(".");
        return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : "");
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
        switch (name) {
          case "amount_no_decimals":
            return withDelimiters(0, ",", ".");
          case "amount_with_comma_separator":
            return withDelimiters(2, ".", ",");
          case "amount_no_decimals_with_comma_separator":
            return withDelimiters(0, ".", ",");
          case "amount_with_apostrophe_separator":
            return withDelimiters(2, "'", ".");
          default:
            return withDelimiters(2, ",", ".");
        }
      });
    }
  }

  window.customElements.define("kit-addons", KitAddons);
}
